/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext, useMemo, useState, useRef,
  useEffect,
} from 'react';
import { MaterialReactTable, useMaterialReactTable,
  MRT_Cell,
  MRT_Row,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  MRT_BottomToolbar,
  MRT_TableContainer,
  MRT_TableHeadCellFilterContainer,
  MRT_TopToolbar,
} from 'material-react-table';
import { TFunction, useTranslation } from 'react-i18next';
import { StoreContext } from 'models/import';
import Pill, { PillModifier } from 'components/pill';
import {
  ValidationDetail,
  ValidationTableDef,
  ValidationSelectOption,
  ValidationMaterialTableProps,
  FlowNegativeItem
} from 'models/validation';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from 'components/tooltip';
import { TableDataRes } from '../base/tableBaseApi';
import ValidationSectionSelectorComponent from 'pages/validation/components/validationSectionSelector';
import { isTrade, getAccountBalance, getAccountBalanceAfter, getAccountBalanceByExchange, getAccountBalanceAfterByExchange, getTransactionType, getDateForValidation, getExchanges, getBuy, getSell, getDateForUI } from 'services/transactions';
import { useGetValidationRequest } from 'pages/validation/network/validationRequest';
import { getSecondaryStripedRowColor, getSecondaryStripedRowsForExpansion, getStripedRowsForExpansion } from 'services/util';
import { getDefaultMRTOptions } from '../materialTable';
import { Button } from '@mui/material';
import ValidationFlowNegativesTableFilters, { ShowPreviousTransactions, ValidationFlowNegativesTableFilterValues } from '../filters/validationFlowNegativesTableFilters';
import useStateLocalStorage from 'hooks/useStateLocalStorage';

  export default function ValidationFlowNegativesTable(props: ValidationMaterialTableProps) {
    const { t } = useTranslation();
    const { userSettings } = useContext(StoreContext);

    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
      [],
    );
    const [filters, setFilters] = useStateLocalStorage<ValidationFlowNegativesTableFilterValues>('review_flow_filter_v2_'+(userSettings?.userId || Date.now()),
      {currencies:0,match_same_exchange:true,amount_accuracy:90,time_accuracy:2,already_matched:false,show_previous_transactions:ShowPreviousTransactions.SHOW_ALL_TRANSACTIONS}
    );
    const [showColumnFilters, setShowColumnFilters] = useState(false);

  const isTradeFlowNegative = (value: FlowNegativeItem): boolean => {
    //trades not supported for flow negatives
    return false;
  };

  const getTransactionTypeFlowNegative = (
    typeId: number | null, 
    value: FlowNegativeItem,
    allOptions: ValidationSelectOption[],
    ): string => {
    const allArr = allOptions.map((val) => val.value);
    if ((!typeId && typeId !== 0) || !allArr.includes(typeId)) {
      return '';
    }

    let newId = typeId;

    if (isTradeFlowNegative(value)) {
      newId = -1;
    }

    const result = allOptions
      .filter((val) => val.value === newId)
      .map((val) => val.text);

    if (result.length === 1) {
      return result[0];
    }
    return '';
  };

  const getDateFlowNegative = (
    numberValue: number | null, 
    dateFormat: string | undefined,
    value: FlowNegativeItem,
    )
  : string | null => {
    // if trade we return null
    if (!numberValue || isTradeFlowNegative(value)) {
      return null;
    }
    return getDateForUI(numberValue,dateFormat);
  };

  const onDetailNavigate = (id: number | null): void => {
    if (!id) { return; }
    
    //We need to use jquery to fake a form submit with post
    $('<form method="POST" target="_blank" action="/enter_coins.php"><input type="hidden" name="edit_tx" value="'+id+'" /></form>')
    .appendTo('body').submit().remove();
  };

  //Expansion logic
    const canExpand = (value: FlowNegativeItem): boolean => {
    if (value && value.transaction && value.transaction.id) { // is defined
    if (previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_PREVIOUS_TRANSACTIONS && value.before_negative && value.before_negative.length > 0) {
      return true;
    } else if(previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_FOLLOWING_TRANSACTIONS && value.after_negative && value.after_negative.length > 0) {
      return true;
    } else if(previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_ALL_TRANSACTIONS &&
      ((value.before_negative && value.before_negative.length > 0)
      || (value.after_negative && value.after_negative.length > 0))
    ) {
      return true;
    } else {
      return false;
    }
  }

  return false;
};

  const previousTransactionExpansionStatus = useMemo(() => {
  if (filters.hasOwnProperty('show_previous_transactions')) {
    return filters.show_previous_transactions;
  }
  return ShowPreviousTransactions.SHOW_ALL_TRANSACTIONS;
  }, [JSON.stringify(filters)]);

 const getSubRowItems = (row: FlowNegativeItem): FlowNegativeItem[] | undefined => {
  if (row && row.transaction && row.transaction.id && row.before_negative && row.after_negative) {
    if (previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_PREVIOUS_TRANSACTIONS) {
      return row.before_negative;
    } else if (previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_FOLLOWING_TRANSACTIONS) {
      return row.after_negative;
    } else if (row.before_negative.length > 0 && row.after_negative.length > 0) {
      //Both so also show original transaction in between
      return [
        ...row.before_negative,
        ...[{
            ...row,
            transaction: {
                ...row.transaction,
            },
            previous_transaction: null,

            //subrows
            before_negative: [],
            after_negative: [],

            is_parent_copy: true,
        }],
        ...row.after_negative,
      ];
    } else if (row.before_negative.length > 0 || row.after_negative.length > 0) {
      //Only 1 so no need to show original transaction
      return [
        ...row.before_negative,
        ...row.after_negative,
      ];
    } else {
      return [];
    }
  } else {
    return [];
  }
};

 const getRowId = (
  row: FlowNegativeItem,
  index: number,
  parentRow: FlowNegativeItem | null = null,
  parentIndex: number | null = null,
  ): string => {
    var rowId = `index=${index};`;
    if (row && row.transaction && row.transaction.id) {
      rowId += `id=${row.transaction.id};`
    }
    //handle subrows
    if (parentIndex) {
      rowId += `parentIndex=${parentIndex};`;
    }
    if (parentRow && parentRow.transaction && parentRow.transaction.id) {
      rowId += `parentId=${parentRow.transaction.id};`
    }
    return rowId;
};

    const {
      data, isError, isFetching, isLoading, refetch,
    } = useGetValidationRequest(
        "validationFlowNegativesTable",
        "/reports/tax/validation/transaction-flow",
        props.setIsFetching,
        columnFilters, 
        props.globalSearch, 
        pagination.pageIndex, 
        pagination.pageSize, 
        sorting, 
        JSON.stringify(filters),
        (response: TableDataRes<ValidationTableDef, ValidationDetail> | null) => {
          props.onLoadCb(response);
      }
    )

  const tableData = (data?.data || []);
  
    const columns = useMemo<MRT_ColumnDef<FlowNegativeItem>[]>(() => [
        {
            id: 'exchange',
            minSize: 50,
            accessorKey: 'exchange',
            header: t('Account(s)', { ns: 'validation' }),
            Cell: (info) => {
              const exchange: string | null = info.cell.getValue() as string | null;
              if (exchange === null) {
                return null;
              }
      
              return (
                <Tooltip
                cursor={TooltipCursorEnum.HELP}
                align={TooltipAlignEnum.LEFT}
                tooltipChildren={(
                  <b>
                    {exchange}
                  </b>
                )}
                modifiers={[]}
                key={info.column.id}
              >
                <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: '200px' }}
            >
              <b>
                {exchange}
              </b>
            </div>
            </Tooltip>
              );
            },
          },
      {
        id: 'coin',
        accessorKey: 'coin',
        minSize: 50,
        Cell: (info) => {
          const coinTicker: string | null = info.cell.getValue() as string | null;
          if (coinTicker === null) {
            return null;
          }
  
          return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: '150px' }}
            >
              <b>
                {coinTicker}
              </b>
            </div>
          );
        },
        header: t('Ticker', { ns: 'validation' }),
      },
      {
        id: 'transaction_type',
        minSize: 50,
        accessorKey: 'transaction.transaction_type',
        filterVariant: 'multi-select',
        filterSelectOptions: props.transactionTypeSelectOption,
        enableColumnFilter: false,
        header: t('Type', { ns: 'validation' }),
        accessorFn: (row) => getTransactionTypeFlowNegative(row.transaction.transaction_type, row, props.transactionTypeSelectOption),
        Cell: (info) => {
          const type = info.cell.getValue<string>();

          return (
          <span>
            <b>{type}</b>
          </span>
          );
        },
      },
      {
        id: 'transaction_time',
        minSize: 50,
        accessorKey: 'transaction.transaction_time',
        enableColumnFilter: false,
        enableColumnFilterModes: true,
        type: 'date',
        muiFilterTextFieldProps: {
          type: 'date',
        },
        columnFilterModeOptions: [
          'fuzzy',
          'between',
        ],
        header: t('Date', { ns: 'validation' }),
        accessorFn: (row) => getDateFlowNegative(row.transaction.transaction_time, userSettings?.dateFormat, row),
        Cell: (info) => {
          return (
          <div
            className="ct_tableBase-text-wrap-one-line"
            style={{ maxWidth: '110px' }}
          >
            {info.cell.getValue<string>() || ''}

          </div>
        );
        },
      },
      {
        id: 'balance_before',
        accessorKey: 'balance_before',
        minSize: 50,
        header: t('Before', { ns: 'validation' }),
        Cell: (info) => {
          let balance: number | null = info.cell.getValue() as number | null;
          const isParent = info.row.depth === 0;
          const isParentCopy = info.row.original.is_parent_copy || false;
          if (!isParent && !isParentCopy) {
            balance = info.row.original.before || null
          }
          if (balance === null) {
            return null; 
          }

          return (
            <div className="ct_tableBase-text-wrap">{balance.toFixed(2)}</div>
            );
        },
      },
      {
        id: 'balance_after',
        accessorKey: 'balance_after',
        minSize: 50,
        header: t('After', { ns: 'validation' }),
        Cell: (info) => {
          let balance: number | null = info.cell.getValue() as number | null;
          const isParent = info.row.depth === 0;
          const isParentCopy = info.row.original.is_parent_copy || false;
          if (!isParent && !isParentCopy) {
            balance = info.row.original.after || null
          }
          if (balance === null) {
            return null; 
          }
  
          return (
          <div className="ct_tableBase-text-wrap">{balance.toFixed(2)}</div>
          );
        },
      },
      {
        id: 'expand',
        minSize: 10,
        header: t('Expand', { ns: 'validation' }),
        Cell: (info) => {
          const isParent = info.row.depth === 0;
          if (!isParent) {
            return null;
          }

          if (!canExpand(info.row.original) ) {
            return null;
          }

          let title: string;
          if (previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_PREVIOUS_TRANSACTIONS) {
            title = t('Previous', { ns: 'validation' });
          } else if (previousTransactionExpansionStatus === ShowPreviousTransactions.SHOW_FOLLOWING_TRANSACTIONS) {
            title = t('Following', { ns: 'validation' });
          } else {
            title = t('Expand', { ns: 'validation' });
          }

          return (
            <button
            key={`expand_${info.row.original.transaction.id}`}
            onClick={(e) => {
              e.stopPropagation();
              info.row.toggleExpanded();
            }}
            type="button"
            className="ct_btn _link _old _noPadding _smallHeight"
          >
            <Pill
              key={`prev_${info.row.original.transaction.id}`}
              value={title}
              modifiers={[PillModifier.GREEN]}
            />
          </button>
          );
        },
      },
    ], [previousTransactionExpansionStatus]);

    const defaultMRTOptions = getDefaultMRTOptions<FlowNegativeItem>();
  
    const table = useMaterialReactTable({
      ...defaultMRTOptions,
      columns,
      data: tableData,
      initialState: {
        ...defaultMRTOptions.initialState,
        columnVisibility: {
            'mrt-row-expand': false,
            before_balance: false,
            after_balance: false,
            before_balance_by_exchange: false,
            after_balance_by_exchange: false,
          },
      },
      muiTableBodyRowProps: (props) => ({
        ...defaultMRTOptions.muiTableBodyRowProps(props),
        onClick: (event: Event) => {
         if (isFetching) return;
            event.stopPropagation();
            onDetailNavigate(props.row.original.transaction.id);
        },
         sx: { 
            cursor: 'pointer',
            '& td': {
              backgroundColor: props.row.original.is_parent_copy ? getSecondaryStripedRowsForExpansion(props.row) : getStripedRowsForExpansion(props.row),
            },
         },
      }),
      muiToolbarAlertBannerProps: isError
        ? {
          color: 'error',
          children: t('Error loading data'),
        }
        : undefined,
      onColumnFiltersChange: setColumnFilters,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      rowCount: data?.meta?.total_item_count ?? 0,
      defaultColumn: {
         ...defaultMRTOptions.defaultColumn,
        minSize:10,
        size: 70,
      },
      state: {
        ...defaultMRTOptions.state,
          columnFilters: columnFilters,
          globalFilter: props.globalSearch,
          isLoading,
          pagination,
          showAlertBanner: isError,
          showSkeletons: isFetching || isLoading,
          sorting,
      },
      muiExpandButtonProps: ({
        row,
      }) => ({
        sx: {
          display: (canExpand(row.original)) ? 'flex' : 'none',
        },
      }),
      muiFilterTextFieldProps: {
        sx: { m: '0.5rem 0', width: '100%' },
      },
      getSubRows: (row) => getSubRowItems(row) ,
      getRowId: (row, index, parent) => {
        if (parent) {
          return getRowId(row, index, parent.original, parent.index) 
        } else {
          return getRowId(row, index) 
        }
      },
      renderTopToolbarCustomActions: () => (
        <p />
      ),
      renderToolbarInternalActions: ({table}) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <ValidationFlowNegativesTableFilters 
              filters={filters}
              onFitlerCB={(e) => setFilters(e)}
              disabled={isFetching || isLoading}
              tooltipAlignment={null}
              isHidden={false}
              />
        </div>
      ),
      enableExpanding: true,
    });

    return (
      <>
        <div className='ct_transactionTable'>
        <MRT_TopToolbar table={table} />
        <div className='ct_transactionTableMain'>
          <MRT_TableContainer table={table} />
        </div>
        <MRT_BottomToolbar table={table} />
     </div>
      </>
    );
  }